import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Backdrop, Fade, Box, Grid, Tooltip, tooltipClasses } from '@mui/material';
import { makeStyles } from "@mui/styles";
import { styled } from '@mui/material/styles';

import { UseIsAdmin, UseUserDetails, UseIsPrime, UseCompanyId, UseUserId } from "../../hooks/auth";
import {
  UseCompanyLogo,
  UseCompanyName,
  UseCompanyIndustry,
  UseSelectedWorkspace,
} from "../../hooks/company";
import SoftTypography from "../SoftTypography";
import SoftBox from "../SoftBox";
import "./heroStyles.css";
import Avatar from "../Avatar";
import DynamicComponent, { Components } from "../../pages/Widgets/components";
import useMediaQuery from "@mui/material/useMediaQuery";
import PlaybookBook3D from "./PlaybookBook3D";
import PlaybookLinkSection from "./PlaybookLinkSection";
import SoftButton from "../SoftButton";
import { useNavigate } from "react-router-dom";
import PageHeaderBg from "../../img/gamesettings/mainSection-min.png";
import PageHeaderBg2 from "../../img/gamesettings/truckBg.png";
import AddIcon from "@mui/icons-material/Add";
import { glassModalPaperStyle, GlassModalCloseButton, glassModalDividerStyle } from "../GlassModal";
import { SUBMIT_BGIMAGE, GET_BACKGROUND } from "../../api/group";
import { UPLOAD_PLAYBOOK_IMAGE } from "../../api/playbook";
import { GET_IMAGE_URL } from "../../helpers/images";
import { FETCH_QUOTES } from "../../api/quotes";
import { useDispatch } from "react-redux";
import { refetchUserData } from "../../redux/auth/actions";
import { TOGGLE_ON_WIDGET, TOGGLE_OFF_WIDGET, ADD_WIDGETS_TO_LIST } from "../../api/userWidgets";
import { fetchReadableDashboards } from "../../pages/Widgets/components/dashboardAccess";
import { isOnHome, fitHomePanel, HOME_DASHBOARD_LIMIT } from "../../pages/Widgets/components/homeDashboards";
import toast from "react-hot-toast";
import GmbWelcomeLogo from "../../img/dashboard-welcomegmb.png";
import GreenToggle from "../GreenToggle";
import { borderRadius, minWidth } from "@mui/system";

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 425,
    background: 'linear-gradient(180deg, #101828 0%, #39558E 100%)',
    boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.25)',
    color: '#101828',
  },
});

const CustomTooltipContent = React.forwardRef(({ closeTooltip, onNextTooltip }, ref) => (
  <SoftBox ref={ref} sx={{ p: 2, textAlign: 'left !important' }}>
    <SoftTypography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 'bold', color: "#FFFFFF" }}>
      Widgets for Quick Insights
    </SoftTypography>
    <SoftTypography variant="body2" sx={{color: "#FFFFFF"}}>
      Widgets provide at-a-glance insights! Customize your<br/> dashboard with Widgets to monitor essential metrics and achievements.
    </SoftTypography>
    <SoftBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: "2rem" }}>
      <SoftTypography variant="body2" sx={{color: "#FFFFFF"}}>
        1/3
      </SoftTypography>
      <SoftBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SoftTypography variant="body2" sx={{color: "#FFFFFF", marginRight: "0.5rem", cursor: "pointer"}} onClick={closeTooltip}>
          Skip Tutorial
        </SoftTypography>
        <SoftButton
          variant="outlined"
          onClick={() => {
            closeTooltip();
            onNextTooltip();
          }}
          sx={{
            background: "#FFFFFF",
            borderRadius: "4px",
            border: "1px solid #EAECF0",
            color: "#2152FF !important",
            textTransform: "capitalize !important",
            paddingTop: "4px",
            paddingBottom: "4px",
          }}
        >
          Next
        </SoftButton>
      </SoftBox>
    </SoftBox>
  </SoftBox>
));

const getTimeBasedGreeting = (time) => {
  if (!time) return "Hello";
  const hours = time.getHours();
  if (hours < 12) return "Good Morning";
  if (hours < 17) return "Good Afternoon";
  return "Good Evening";
};

// embedded: rendered inside the customizable dashboard grid, which provides
// the page gutters itself — strip the baked-in full-bleed paddings (desktop
// only) and fill the grid cell exactly, so hero edges + gaps line up with
// every other block. Mobile keeps the original spacing.
// section: "main" (greeting card only) | "widgets" (My Widgets panel only) —
// lets the dashboard treat the two halves as independent blocks. Default
// renders both, exactly as before.
const Hero = ({ triggerTooltip, onNextTooltip, closeTooltip, embedded, section }) => {
  const user = UseUserDetails();
  const logo = UseCompanyLogo();
  const companyName = UseCompanyName();
  const industry = UseCompanyIndustry();
  const isAdmin = UseIsAdmin();
  const workspace = UseSelectedWorkspace();
  const navigate = useNavigate();
  const [reloadKey, setReloadKey] = useState(0);

  const [userWidget, setUserWidget] = useState([]);
  const [quoteWidget, setQuoteWidget] = useState({});
  const [time, setTime] = useState(null);
  const xsBp = useMediaQuery((theme) => theme.breakpoints.up("xs"));
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isMidsizeScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const screenWidth = window.innerWidth;

  const [openModal, setOpenModal] = useState(false);

  const dispatch = useDispatch();
  const userId = UseUserId();
  const companyId = UseCompanyId();

  const handleImageClick = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageSelect = (imageName) => {
    setSelectedImage(imageName);
  };

  const images = [
    { name: 'PageHeaderBg', src: PageHeaderBg },
    { name: 'PageHeaderBg2', src: PageHeaderBg2 },
  ];

  const { _id: groupId } = UseSelectedWorkspace();
  const [workspaceData, setWorkspaceData] = useState(null);
  const fetchData = async () => {
    try {
      const response = await GET_BACKGROUND(groupId);
      setWorkspaceData(response.data);  
    } catch (error) {
      console.error("Error fetching the award data:", error);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [groupId]);

  // The book button opens this workspace's external playbook link. The
  // in-app /playbook library is parked for now (the page is still routed).
  const playbookUrl = workspaceData?.playbookUrl || "";
  const openPlaybook = () => {
    if (playbookUrl) {
      window.open(playbookUrl, "_blank", "noopener,noreferrer");
    } else if (isAdmin) {
      setOpenModal(true);
    } else {
      toast("No playbook linked for this workspace yet", { icon: "📘" });
    }
  };

  const submitImage = async () => {
    if (selectedImage) {
      try {
        const body = {
          groupId,
          backgroundImage: selectedImage,
        };
        await SUBMIT_BGIMAGE(body);
        fetchData();
        setOpenModal(false);
        setSelectedImage(null);
      } catch (error) {
        console.error("Error submitting background image:", error);
      }
    } else {
      console.warn("No image selected");
    }
  };

  // Upload a custom background — image, GIF, or video. The file goes to blob
  // storage; we save the returned URL as this workspace's backgroundImage.
  // GIFs loop natively as a CSS background; videos loop via the <video loop>
  // element rendered behind the hero (see isVideoBg below).
  const bgFileInputRef = useRef(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [bgUrl, setBgUrl] = useState("");

  // "Other route": paste a hosted image/GIF/video URL directly. Saves via the
  // existing SUBMIT_BGIMAGE endpoint (already deployed), skipping the upload
  // route entirely. GIFs loop natively; videos loop via the <video> element.
  const handleApplyBgUrl = async () => {
    const url = bgUrl.trim();
    if (!url) return;
    setUploadingBg(true);
    try {
      await SUBMIT_BGIMAGE({ groupId, backgroundImage: url });
      await fetchData();
      setOpenModal(false);
      setSelectedImage(null);
      setBgUrl("");
      toast.success("Background updated");
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message;
      toast.error(
        status
          ? `Couldn't apply (${status})${serverMsg ? `: ${serverMsg}` : ""}`
          : `Couldn't apply: ${serverMsg || "network error"}`,
      );
    } finally {
      setUploadingBg(false);
    }
  };

  const handleUploadBgClick = () => bgFileInputRef.current?.click();

  const handleBgFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBg(true);
    try {
      const res = await UPLOAD_PLAYBOOK_IMAGE(file);
      const url = res?.data?.key ? GET_IMAGE_URL(res.data.key) : null;
      if (!url) throw new Error("Upload did not return a URL");
      await SUBMIT_BGIMAGE({ groupId, backgroundImage: url });
      await fetchData();
      setOpenModal(false);
      setSelectedImage(null);
      toast.success("Background updated");
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message;
      console.error("Error uploading background:", status, error?.response?.data || error);
      toast.error(
        status
          ? `Upload failed (${status})${serverMsg ? `: ${serverMsg}` : ""}`
          : `Upload failed: ${serverMsg || "network error"}`,
      );
    } finally {
      setUploadingBg(false);
      if (bgFileInputRef.current) bgFileInputRef.current.value = "";
    }
  };

  const bgValue = workspaceData?.backgroundImage;
  const presetSrc = images.find((img) => img.name === bgValue)?.src;
  // A custom upload is stored as a full URL ("http…") or an app path ("/…").
  // Videos are detected by extension so we can loop them behind the hero;
  // GIFs/images fall through to the normal CSS background (GIFs loop natively).
  const isCustomUrl =
    typeof bgValue === "string" && (bgValue.startsWith("http") || bgValue.startsWith("/"));
  const isVideoBg =
    typeof bgValue === "string" && /\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test(bgValue);
  const workspaceBg =
    bgValue === "primary"
      ? "primary"
      : presetSrc || (isCustomUrl ? bgValue : PageHeaderBg);

  // Background for the hero surface: solid behind a looping video, the theme's
  // radial gradient for "primary", otherwise the image/GIF as a CSS background.
  const heroBg = (theme) => {
    if (isVideoBg) return "#101828";
    if (workspaceBg === "primary") return theme.palette.radialGradient;
    return `url(${workspaceBg})`;
  };

  // Standard GMB frosted-glass modal — light, TRUE center, compact. Shared
  // with other dashboard modals via GlassModal so they read as one system.
  // Approved prototype: compact ~400px paper.
  const styleModal = glassModalPaperStyle({ mini: "92%", xs: "92%", sm: "400px" });

  /* Sample cards for the empty panel. They are indistinguishable from real
     widgets on screen, so they FILL the three slots rather than replacing
     them: activating your first widget used to make all three disappear at
     once, which reads as "my widgets were deleted". */
  const DefaultWidgets = [
    { key: "ranking", Widget: Components.ranking, index: 0 }, // RankingComponent
    { key: "top_kpi", Widget: Components.top_kpi, index: 1 }, // TopKPIComponent
    { key: "level", Widget: Components.level, index: 2 } // LevelComponent
  ];

  useEffect(() => {
    // Real-time clock update
    const currentTime = new Date();
    const interval = setInterval(() => setTime(new Date()), 60 * 1000); // Update every minute only
    setTime(currentTime);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setReloadKey(Math.round(Math.random() * 1000));
    };

    // Window resize listener to refresh (and resize) avatar
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchQuotesAndSetWidgets = async () => {
      if (user && user.widgets && user.widgets.length) {
        const userWidgets = [];
        let quoteData = null;

        const widgetPromises = user.widgets.map(async (widget) => {
          if (widget.key === "quote_of_the_day") {
            try {
              const response = await FETCH_QUOTES();
              quoteData = response.data;
            } catch (err) {
              console.log(err.response);
            }
          } else {
            userWidgets.push(widget);
          }
        });

        await Promise.all(widgetPromises);

        setUserWidget(userWidgets);

        if (quoteData && quoteData.length > 0) {
          setQuoteWidget(quoteData[0]);
        }
      }
    };

    fetchQuotesAndSetWidgets();
  }, [user]);

  const useStyles = makeStyles(() => ({
    topBoxbg: {
      // position:relative makes the hero card itself the positioned ancestor
      // for the absolutely-positioned Avatar wrapper inside, so the canvas
      // anchors to the card rather than to a height-0 grandchild that ends
      // up positioned below the card.
      position: "relative",
      overflow: "hidden",
      filter: "drop-shadow(0px 1px 3px rgba(16, 24, 40, 0.1)) drop-shadow(0px 1px 2px rgba(16, 24, 40, 0.06))",
      paddingTop: { mini: "101.25px", xs: "109.25px", md: "75px" },
      // paddingBottom: "2rem",
      borderRadius: "12px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      maxHeight: { mini: "960px", xxs: "1000px", md: "850px" },
      animation: "$bgAnimations 45s infinite alternate",
    },
    "@keyframes bgAnimations": {
      "0%": {
        backgroundPosition: "left",
      },
      "50%": {
        backgroundPosition: "right",
      },
      "100%": {
        backgroundPosition: "center center",
      },
    },
  }));

  const classes = useStyles();

  useEffect(() => {
    const fetchQuotesAndSetWidgets = async () => {
      if (user && user.widgets && user.widgets.length) {
        const userWidgets = [];
        let quoteData = null;

        const widgetPromises = user.widgets.map(async (widget) => {
          if (widget.key === "quote_of_the_day") {
            try {
              const response = await FETCH_QUOTES();
              quoteData = response.data;
            } catch (err) {
              console.log(err.response);
            }
          } else {
            userWidgets.push(widget);
          }
        });

        await Promise.all(widgetPromises);

        setUserWidget(userWidgets);

        if (quoteData && quoteData.length > 0) {
          setQuoteWidget(quoteData[0]);
        }
      }
    };

    fetchQuotesAndSetWidgets();
  }, [user]);

  /* A pinned dashboard card draws nothing once the board stops being shared
     with this player. Dropping those here rather than inside the card means
     a dead pin doesn't silently eat one of the three visible slots. */
  const [readableDashIds, setReadableDashIds] = useState(null);
  const hasDashboardPin = userWidget.some((w) => w.key === "dashboard_link");
  useEffect(() => {
    if (!hasDashboardPin) return undefined;
    let alive = true;
    fetchReadableDashboards().then((rows) => {
      if (!alive || rows === null) return;
      setReadableDashIds(new Set(rows.map((d) => String(d._id))));
    });
    return () => {
      alive = false;
    };
  }, [hasDashboardPin]);

  const visibleWidgets = useMemo(() => {
    if (!hasDashboardPin) return userWidget;
    // Still checking — hold the dashboard cards back rather than flashing
    // one that turns out to be gone.
    if (!readableDashIds) return userWidget.filter((w) => w.key !== "dashboard_link");
    /* Only two boards get a home slot. The cap is applied AFTER the access
       check so a board that stopped being shared can't hold one of the two. */
    let boardsKept = 0;
    return userWidget.filter((w) => {
      if (w.key !== "dashboard_link") return true;
      if (!readableDashIds.has(String(w.dashboardId)) || !isOnHome(w)) return false;
      boardsKept += 1;
      return boardsKept <= HOME_DASHBOARD_LIMIT;
    });
  }, [userWidget, hasDashboardPin, readableDashIds]);

  /* The panel always shows three cards: your real widgets first, then sample
     ones to fill what's left. Padding rather than swapping is what stops a
     first activation from looking like the other cards were wiped. A sample
     is skipped when you already own that same widget, so nothing doubles up. */
  const panelSlots = useMemo(() => {
    const real = fitHomePanel(visibleWidgets);
    const taken = new Set(real.map((w) => w.key));
    const fillers = DefaultWidgets.filter((d) => !taken.has(d.key)).slice(0, 3 - real.length);
    return [
      ...real.map((widget) => ({ id: widget._id || widget.key, widget })),
      ...fillers.map((d) => ({ id: `sample-${d.key}`, Placeholder: d.Widget })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleWidgets]);

  const handleActivateQuote = async () => {
    try {
      const payload = {
        userId,
        companyId,
        key: "quote_of_the_day",
      };
  
      await TOGGLE_ON_WIDGET(payload);
      dispatch(refetchUserData(userId));
      setTimeout(() => {
        toast.success("Quote of the Day widget activated successfully");
      }, 1000);
  
      const response = await FETCH_QUOTES();
      setQuoteWidget(response.data[0]);
    } catch (error) {
      console.error("Error activating quote widget:", error);
      if (error.response?.data?.error?.message === "Ask your company admin to set this widget") {
        try {
          const quoteResponse = await FETCH_QUOTES();
          const quote = quoteResponse.data[0];
          
          const savePayload = {
            userId,
            key: "quote_of_the_day",
            category: "motivation", // Assuming the category is "motivation"
            title: "Quote of the Day",
            companyId,
          };
  
          await ADD_WIDGETS_TO_LIST(savePayload);
          dispatch(refetchUserData(userId));
          setTimeout(() => {
            setQuoteWidget(quote);
            toast.success("Quote of the Day widget activated successfully");
          }, 1000);
        } catch (saveError) {
          console.error("Error saving quote widget:", saveError);
          toast.error("Failed to save Quote of the Day widget");
        }
      } else {
        toast.error(error.response?.data?.error?.message || "Something went wrong!");
      }
    }
  };

  const handleDeactivateQuote = async () => {
    try {
      const quoteWidgets = user.widgets.find(widget => widget.key === "quote_of_the_day");
      if (!quoteWidgets) {
        console.error("Quote widget not found");
        return;
      }

      await TOGGLE_OFF_WIDGET(quoteWidgets._id, userId);
      dispatch(refetchUserData(userId));
      setQuoteWidget(null);
      toast.success("Quote of the Day widget deactivated successfully");
    } catch (error) {
      console.error("Error deactivating quote widget:", error);
      toast.error(
        error.response?.data?.error?.message || "Something went wrong!"
      );
    }
  };
  
  const [openTooltip, setOpenTooltip] = useState(false);

  useEffect(() => {
    if (triggerTooltip) {
      setOpenTooltip(true);
    }
  }, [triggerTooltip]);

  const hideTooltip = () => {
    setOpenTooltip(false);
    closeTooltip();
  }

  return (
    <Box sx={embedded ? { height: "100%" } : undefined}>
      <Grid container sx={embedded ? { height: "100%" } : undefined}>
        <Grid item mini={12} md={12} lg={section === "main" ? 12 : 9} sx={section === "widgets" ? { display: "none" } : undefined}>
          <SoftBox
            sx={{
              height: { mini: "736px", xxs: "736px", md: embedded ? "100%" : "625px" },
              padding: { mini: "0", md: embedded ? "0" : "1.5rem" },
              // Standalone Hero owns the Feed-style gutters. When embedded in
              // the customizable board, Homepage SoftBox marginX already does.
              paddingLeft: { mini: embedded ? "0" : "1rem", sm: embedded ? "0" : "1.5rem", md: embedded ? "0" : "1.5rem" },
              // The 32px right gutter separates the greeting from My Widgets —
              // drop it when the greeting renders alone as its own block.
              paddingRight: {
                mini: embedded ? "0" : "1rem",
                sm: embedded ? "0" : "1.5rem",
                md: section === "main" ? "0" : "32px",
              },
              paddingTop: { mini: "0", md: embedded ? "0" : "24px" },
              ...(isSmallScreen && ({
                minHeight: "85vh",
                height: "100%",
                marginBottom: embedded ? 0 : "16px",
              })),
            }}
          >
            <SoftBox className={classes.topBoxbg}
              sx={{
                position: "relative",
                overflow: "hidden",
                background: heroBg,
                backgroundRepeat: "no-repeat",
                borderRadius: "12px",
                ...(isSmallScreen && ({ filter: "none !important" })),
                ...(!isVideoBg && !workspaceBg.includes("mainSection-min") && {
                  backgroundSize: isSmallScreen ? "cover" : "100%",
                })
              }}
            >
              {isVideoBg && (
                <SoftBox
                  component="video"
                  src={workspaceBg}
                  autoPlay
                  muted
                  loop
                  playsInline
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "12px",
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />
              )}
              <div style={{
                position: "relative",
                zIndex: 1,
                background: (workspaceBg === 'primary' || isVideoBg) ? 'none' : `linear-gradient(${isSmallScreen ? 155.85 : 100.85}deg, #F9FAFB 25.18%, rgba(247, 248, 249, 0.74) 40.61%, rgba(238, 239, 240, 0.47) 59.75%, rgba(249, 250, 252, 0) 94.73%)`,
                borderRadius: "12px",
                height: "inherit",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                }}
              >
                <SoftBox
                  sx={{
                    paddingLeft: { mini: "1.5rem", md: "2.5rem" },
                    // Desktop: align the greeting's top with the edit icon
                    // (which sits at top: 1.25rem on the far right).
                    paddingTop: { mini: "2rem", md: "1.25rem" },
                    paddingRight: { mini: "1.5rem", md: "2.5rem" },
                  }}
                >
                  <SoftTypography
                    sx={{
                      background:
                        "radial-gradient(100% 784.37% at 0% 50%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.9) 25%, #FFF 50%, rgba(255, 255, 255, 0.9) 75%, rgba(255, 255, 255, 0.3) 100%, rgba(255, 255, 255, 0.2) 100%)",
                      backgroundClip: "text",
                      textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main',
                      fontWeight: 600,
                      fontSize: { mini: "21px", sm: "22px", md: screenWidth === 1024 ? "20px" : "24px" },
                      lineHeight: 1.2,
                    }}
                  >
                    {getTimeBasedGreeting(time)}, {user?.firstName ?? "Player"}
                  </SoftTypography>
                  {(!isSmallScreen)
                    ? <SoftTypography sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main', marginTop: "0.75rem", fontSize: "14px", fontWeight: 500, opacity: 0.85 }}>
                        <span style={{cursor: "pointer"}} onClick={openPlaybook}>{(isAdmin && !playbookUrl) ? "Link Playbook" : "View Playbook"}</span>{isAdmin && (<> &middot; <span style={{cursor: "pointer"}} onClick={() => navigate("/settings/players")}>Manage Players</span></>)}
                      </SoftTypography>
                    :  <SoftTypography
                          sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main', fontWeight: '500', fontSize: "14px", color: "#667085" }}
                          variant="body2"
                        >
                          {time &&
                            time.toLocaleString("en-US", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                              hour12: true,
                            }) + " "}
                      </SoftTypography>
                  }
                </SoftBox>
                <SoftBox sx={{ ...(isSmallScreen && ({ marginTop: "8px", position: "relative" })) }}>
                  <SoftBox
                    sx={{
                      position: "absolute",
                      // Settle the avatar with a small lift off the card
                      // floor — enough breathing room, not so much that it
                      // crowds the top.
                      bottom: { mini: 0, md: "2.25rem" },
                      right: screenWidth === 1024 ? 45 : 0,
                      width: {
                        mini: "100vw",
                        md: "50vw",
                        lg: "50vw",
                        xl: "50vw"
                      },
                      height: {
                        mini: "58vh",
                        md: screenWidth === 1024 ? "45vw" : "470px",
                      },
                      // MOBILE ONLY: sit the avatar in normal flow and let it
                      // fill the middle of the card (flex:1) so the whole
                      // character — head to feet — is centred and visible,
                      // instead of being anchored to the top and clipped.
                      // Desktop keeps the absolute layout above, untouched.
                      ...(isSmallScreen && {
                        position: "relative",
                        bottom: "auto",
                        right: "auto",
                        width: "100%",
                        height: "60vh",
                      }),
                    }}
                  >
                    <Avatar
                      key={`Avatar${reloadKey}`}
                      url={user.avatar}
                      logo={logo}
                      industry={industry}
                      noControls={true}
                    />
                  </SoftBox>

                {(isSmallScreen && quoteWidget?.q) &&
                  <SoftBox sx={{ position: "relative", padding: "0 24px 12px" }}>
                    <SoftTypography
                      variant="body1"
                      sx={{
                        textFillColor: workspaceBg === 'primary' ? 'white' : '#1D2939',
                        maxWidth: "600px",
                        fontSize: "14px",
                        fontWeight: 500,
                        fontStyle: "normal",
                        lineHeight: "20px",
                        marginBottom: "8px",
                      }}
                    >
                      "{quoteWidget.q}"
                    </SoftTypography>
                    <SoftTypography
                      sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : '#1D2939', fontSize: "16px", fontFamily: "DM Sans", fontWeight: 400, lineHeight: "16px" }}
                      variant="body2"
                    >
                      {quoteWidget.a}
                    </SoftTypography>
                  </SoftBox>
                }
                </SoftBox>
                <Box
                  sx={{
                    width: "100%",
                    paddingX: { mini: "1.5rem", md: "2.5rem" },
                    paddingBottom: { mini: "1.5rem", md: "1.75rem" },
                  }}
                >
                  <SoftBox
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <SoftBox
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        ...(isSmallScreen && ({
                          maxWidth: "100%",
                          display: "unset",
                        })),
                      }}
                    >
                      {quoteWidget && quoteWidget.q ? (
                        !isSmallScreen &&
                        <SoftBox sx={{maxWidth: "45%"}}>
                          <SoftBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: { mini: "0.25rem", md: "0.5rem" },
                            }}
                          >
                            <SoftTypography
                              variant={xsBp ? "h1" : "h2"}
                              sx={{
                                textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main',
                                maxWidth: "600px",
                                fontSize: "16px",
                                fontWeight: "500",
                                fontStyle: "normal",
                                lineHeight: "28px",
                              }}
                            >
                              "{quoteWidget.q}" - {quoteWidget.a}
                            </SoftTypography>
                          </SoftBox>
                          {(!isSmallScreen) &&
                            <SoftTypography
                              sx={{
                                textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main',
                                fontWeight: '500',
                                fontSize: "13px",
                                opacity: 0.85,
                                marginTop: "0.75rem",
                              }}
                              variant="body2"
                            >
                              {time &&
                                time.toLocaleString("en-US", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                  hour: "numeric",
                                  minute: "numeric",
                                  hour12: true,
                                }) + " "}
                            </SoftTypography>
                          }
                        </SoftBox>
                      ) : (
                        <SoftBox>
                          <SoftTypography
                            sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main', fontWeight: '500', fontSize: "15px" }}
                            variant="body2"
                          >
                            {(!isSmallScreen && time) &&
                              time.toLocaleString("en-US", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                              }) + " "
                            }
                            {workspace?.name ?? companyName}
                          </SoftTypography>
                        </SoftBox>
                      )}

                      {(!isSmallScreen) && (
                        <>
                          <SoftButton
                            variant="contained"
                            sx={{
                              // Frosted-glass coaster so the book sits on something
                              // and lifts off the blue hero (glow lives on the book).
                              background: 'radial-gradient(circle at 50% 42%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 58%, rgba(255,255,255,0) 74%)',
                              backdropFilter: 'blur(6px)',
                              WebkitBackdropFilter: 'blur(6px)',
                              border: '1px solid rgba(255,255,255,0.20)',
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28), 0 8px 18px -10px rgba(0,0,0,0.40)',
                              height: "max-content",
                              width: "max-content",
                              minWidth: "max-content",
                              minHeight: "max-content",
                              borderRadius: "50%",
                              padding: "0.6rem",
                              alignSelf: "flex-end",
                              position: "relative",
                              // Keep the glass look on hover — the book opening is the feedback.
                              "&:hover": {
                                background: 'radial-gradient(circle at 50% 42%, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0.08) 58%, rgba(255,255,255,0) 74%)',
                              },
                              "&:focus:not(:hover)": {
                                background: 'radial-gradient(circle at 50% 42%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 58%, rgba(255,255,255,0) 74%)',
                              },
                            }}
                            onClick={openPlaybook}
                            aria-label={playbookUrl ? "Open playbook" : "No playbook linked"}
                          >
                            <PlaybookBook3D />
                            {playbookUrl && (
                              // "Equipped" pip — tells players the book leads somewhere.
                              <SoftBox
                                component="span"
                                sx={{
                                  position: "absolute",
                                  top: "10px",
                                  right: "10px",
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                  background: "#12a150",
                                  border: "2px solid rgba(255,255,255,0.9)",
                                  boxShadow: "0 2px 4px rgba(16,24,40,0.25)",
                                  pointerEvents: "none",
                                }}
                              />
                            )}
                          </SoftButton>
                          {(isAdmin) && (
                          <>
                          <SoftButton
                            variant="contained"
                            sx={{
                              background: 'linear-gradient(180deg, rgba(52, 64, 84, 0.25) 0%, rgba(115, 142, 186, 0.25) 100%)',
                              border: '1px solid rgba(102, 112, 133, 0.2)',
                              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.16), inset 4px 0px 4px rgba(83, 81, 81, 0.25), inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
                              height: "max-content",
                              width: "max-content",
                              minWidth: "max-content",
                              minHeight: "max-content",
                              borderRadius: "50%",
                              padding: "0.5rem",
                              // Anchor to the hero card top-right (the topBoxbg
                              // wrapper is the positioned ancestor).
                              position: 'absolute',
                              top: "1.25rem",
                              right: "1.5rem",
                              zIndex: 5,
                              "&:hover": {
                                background: (theme) => theme.palette.tertiary.dark,
                              },
                              "&:focus:not(:hover)": {
                                background: (theme) => theme.palette.tertiary.dark,
                              },
                            }}
                            onClick={isAdmin ? handleImageClick : undefined}
                            >
                            <svg width="31" height="30" viewBox="0 0 31 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{zIndex: "1000"}}>
                              <g clipPath="url(#clip0_35_4281)">
                              <path d="M7.42498 24.3999C6.66248 24.3999 5.94998 24.1374 5.43748 23.6499C4.78748 23.0374 4.47498 22.1124 4.58748 21.1124L5.04998 17.0624C5.13748 16.2999 5.59998 15.2874 6.13748 14.7374L16.4 3.87494C18.9625 1.16244 21.6375 1.08744 24.35 3.64994C27.0625 6.21244 27.1375 8.88744 24.575 11.5999L14.3125 22.4624C13.7875 23.0249 12.8125 23.5499 12.05 23.6749L8.02498 24.3624C7.81248 24.3749 7.62498 24.3999 7.42498 24.3999ZM20.4125 3.63744C19.45 3.63744 18.6125 4.23744 17.7625 5.13744L7.49998 16.0124C7.24998 16.2749 6.96248 16.8999 6.91248 17.2624L6.44998 21.3124C6.39998 21.7249 6.49998 22.0624 6.72498 22.2749C6.94998 22.4874 7.28748 22.5624 7.69998 22.4999L11.725 21.8124C12.0875 21.7499 12.6875 21.4249 12.9375 21.1624L23.2 10.2999C24.75 8.64994 25.3125 7.12494 23.05 4.99994C22.05 4.03744 21.1875 3.63744 20.4125 3.63744Z" fill="#EAECF0"/>
                              <path d="M22.1751 13.6876C22.1501 13.6876 22.1126 13.6876 22.0876 13.6876C18.1876 13.3001 15.0501 10.3376 14.4501 6.46256C14.3751 5.95006 14.7251 5.47506 15.2376 5.38756C15.7501 5.31256 16.2251 5.66256 16.3126 6.17506C16.7876 9.20006 19.2376 11.5251 22.2876 11.8251C22.8001 11.8751 23.1751 12.3376 23.1251 12.8501C23.0626 13.3251 22.6501 13.6876 22.1751 13.6876Z" fill="#EAECF0"/>
                              <path d="M26.75 28.4375H4.25C3.7375 28.4375 3.3125 28.0125 3.3125 27.5C3.3125 26.9875 3.7375 26.5625 4.25 26.5625H26.75C27.2625 26.5625 27.6875 26.9875 27.6875 27.5C27.6875 28.0125 27.2625 28.4375 26.75 28.4375Z" fill="#EAECF0"/>
                              </g>
                              <defs>
                              <clipPath id="clip0_35_4281">
                              <rect width="30" height="30" fill="white" transform="translate(0.5)"/>
                              </clipPath>
                              </defs>
                            </svg>
                          </SoftButton>
                          <Modal
                            open={openModal}
                            onClose={handleCloseModal}
                            BackdropComponent={Backdrop}
                            BackdropProps={{
                              timeout: 500,
                              // Match the global Dialog backdrop exactly (the
                              // Players-page modals): soft dim + 3px frost.
                              sx: {
                                backgroundColor: "rgba(15,23,42,0.16)",
                                backdropFilter: "blur(3px)",
                                WebkitBackdropFilter: "blur(3px)",
                              },
                            }}
                          >
                            <Fade in={openModal}>
                              <SoftBox sx={styleModal}>
                                <SoftBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <SoftTypography sx={{ fontFamily: "DM Sans", fontSize: "17px", fontWeight: 600, color: "#101828" }} component="h2">
                                    Select your dashboard theme
                                  </SoftTypography>
                                  <GlassModalCloseButton onClick={handleCloseModal} />
                                </SoftBox>
                                <hr style={glassModalDividerStyle}/>
                                <SoftBox sx={{marginTop: '1rem'}}>
                                  <form>
                                    <Grid container style={{
                                      background: '#FFFFFF',
                                      border: '1px solid #E4E7EC',
                                      borderRadius: '8px',
                                      padding: '0.75rem'
                                    }}>
                                      {images.map((image) => (
                                        <Grid item mini={12} md={4} lg={4} key={image.name} style={{padding: '.25rem'}}>
                                          <label style={{
                                            border: selectedImage === image.name ? '2px solid var(--gmb-primary, #1B598E)' : '2px solid transparent',
                                            display: 'block',
                                            cursor: 'pointer',
                                            borderRadius: '8px'
                                          }}>
                                            <input
                                              type="radio"
                                              name="imageselect"
                                              value={image.name}
                                              style={{display: 'none'}}
                                              checked={selectedImage === image.name}
                                              onChange={() => handleImageSelect(image.name)}
                                            />
                                            <img src={image.src} alt="" style={{width: '100%', height: '64px', objectFit: 'cover', display: 'block', borderRadius: '8px'}}/>
                                          </label>
                                        </Grid>
                                      ))}
                                      <Grid item mini={12} md={4} lg={4} style={{padding: '.25rem'}}>
                                        <label style={{
                                          border: selectedImage === 'primary' ? '2px solid var(--gmb-primary, #1B598E)' : '2px solid transparent',
                                          display: 'block',
                                          cursor: 'pointer',
                                          borderRadius: '8px'
                                        }}>
                                          <input
                                            type="radio"
                                            name="imageselect"
                                            value="primary"
                                            style={{display: 'none'}}
                                            checked={selectedImage === 'primary'}
                                            onChange={() => handleImageSelect('primary')}
                                          />
                                          <SoftBox sx={{
                                            width: '100%',
                                            height: '64px',
                                            borderRadius: '8px',
                                            background: (theme) => theme.palette.radialGradient
                                          }} />
                                        </label>
                                      </Grid>
                                    </Grid>
                                  </form>
                                  <SoftBox sx={{ marginTop: "1rem" }}>
                                    <input
                                      ref={bgFileInputRef}
                                      type="file"
                                      accept="image/*,video/mp4,video/webm,video/quicktime"
                                      style={{ display: "none" }}
                                      onChange={handleBgFileChange}
                                    />
                                    <SoftButton
                                      onClick={handleUploadBgClick}
                                      disabled={uploadingBg}
                                      sx={{
                                        width: "100%",
                                        background: "#F9FAFB",
                                        border: "1px dashed #D0D5DD",
                                        borderRadius: "8px",
                                        color: "#475467",
                                        textTransform: "none",
                                        boxShadow: "none",
                                        "&:hover": { background: "#F2F4F7", boxShadow: "none" },
                                      }}
                                    >
                                      {uploadingBg ? "Uploading…" : "Upload image, GIF, or video"}
                                    </SoftButton>
                                    <SoftTypography sx={{ fontSize: "12px", color: "#98A2B3", marginTop: "6px" }}>
                                      Videos and GIFs loop automatically.
                                    </SoftTypography>

                                    <SoftBox sx={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                                      <SoftBox
                                        component="input"
                                        type="text"
                                        placeholder="…or paste an image / GIF / video URL"
                                        value={bgUrl}
                                        onChange={(e) => setBgUrl(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && handleApplyBgUrl()}
                                        sx={{
                                          flex: 1,
                                          height: "40px",
                                          padding: "0 12px",
                                          background: "#FFFFFF",
                                          border: "1px solid #D0D5DD",
                                          borderRadius: "8px",
                                          color: "#191D23",
                                          fontSize: "13px",
                                          outline: "none",
                                          "&::placeholder": { color: "#98A2B3" },
                                        }}
                                      />
                                      <SoftButton
                                        onClick={handleApplyBgUrl}
                                        disabled={uploadingBg || !bgUrl.trim()}
                                        sx={{
                                          background: "#FFFFFF",
                                          border: "1px solid #D0D5DD",
                                          borderRadius: "8px",
                                          color: "#344054",
                                          textTransform: "none",
                                          minWidth: "84px",
                                          boxShadow: "none",
                                          "&:hover": { background: "#F9FAFB", boxShadow: "none" },
                                        }}
                                      >
                                        Apply
                                      </SoftButton>
                                    </SoftBox>
                                  </SoftBox>
                                  <PlaybookLinkSection
                                    open={openModal}
                                    groupId={groupId}
                                    workspaceName={workspace?.name}
                                    currentUrl={playbookUrl}
                                    onSaved={(url) => setWorkspaceData((prev) => ({ ...(prev || {}), playbookUrl: url }))}
                                  />
                                  {/* Approved prototype: the quote setting is one
                                      compact row with a toggle — not a box with
                                      Activate/Deactivate buttons. */}
                                  <SoftBox style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: '12px',
                                      background: '#F9FAFB',
                                      border: '1px solid #EAECF0',
                                      borderRadius: '8px',
                                      padding: '10px 14px',
                                      marginTop: '1rem',
                                    }}>
                                      <SoftBox>
                                        <SoftTypography sx={{fontFamily: 'DM Sans', fontSize: '13px', fontWeight: 500, color: '#101828'}}>
                                          Daily quote
                                        </SoftTypography>
                                        <SoftTypography sx={{fontFamily: 'DM Sans', fontSize: '12px', fontWeight: 400, color: '#667085'}}>
                                          Motivate the team each morning
                                        </SoftTypography>
                                      </SoftBox>
                                      {(() => {
                                        const quoteOn = Boolean(quoteWidget && quoteWidget.q);
                                        return (
                                          <GreenToggle
                                            checked={quoteOn}
                                            onChange={quoteOn ? handleDeactivateQuote : handleActivateQuote}
                                          />
                                        );
                                      })()}
                                  </SoftBox>
                                  <SoftBox sx={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.25rem'}}>
                                    <SoftButton
                                      onClick={handleCloseModal}
                                      sx={{
                                        background: "#FFFFFF",
                                        border: "1px solid #D0D5DD",
                                        borderRadius: "8px",
                                        color: "#344054",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        padding: "0 18px",
                                        height: "40px",
                                        boxShadow: "none",
                                        "&:hover": { background: "#F9FAFB", boxShadow: "none" },
                                      }}
                                    >
                                      Cancel
                                    </SoftButton>
                                    <SoftButton
                                      onClick={submitImage}
                                      sx={{
                                        background: 'var(--gmb-primary, #1B598E)',
                                        border: 0,
                                        borderRadius: "8px",
                                        color: '#FFFFFF !important',
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        padding: "0 20px",
                                        height: "40px",
                                        boxShadow: "none",
                                        "&:hover": { background: 'var(--gmb-primary, #1B598E)', filter: 'brightness(0.96)', boxShadow: "none" },
                                      }}
                                    >
                                      Update
                                    </SoftButton>
                                  </SoftBox>
                                </SoftBox>
                              </SoftBox>
                            </Fade>
                          </Modal>
                          </>
                          )}
                        </>
                    )}
                    </SoftBox>
                  </SoftBox>
                </Box>
              </div>
              <SoftBox
                overflow="hidden"
                position="absolute"
                left="15px"
                sx={{
                  height: { mini: "440px", xxs: "480px", lg: "530px" },
                  display: "flex",
                  justifyContent: "flex-end",
                  right: isSmallScreen ? "50px" : "150px",
                  zIndex: "-1000!important",
                  ...(screenWidth <= 1280 && ({
                    right: "100px"
                  }))
                }}
              >
                <SoftBox
                  sx={{
                    maxWidth: {
                      mini: "50px",
                      xs: "80px",
                      md: "100px",
                      xxl: "120px",
                    },
                    display: "inline-block",
                    position: "relative",
                    width: "full",
                  }}
                >
                  <img
                    src={GmbWelcomeLogo}
                    alt=""
                    style={{ animation: `70s scrollEffect infinite linear`, opacity: 0.45 }}
                  />
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Grid>
        <Grid item mini={12} md={12} lg={section === "widgets" ? 12 : 3} sx={section === "main" ? { display: "none" } : undefined}>
          <CustomWidthTooltip 
            title={<CustomTooltipContent closeTooltip={hideTooltip} onNextTooltip={onNextTooltip} />}
            placement="left"
            arrow
            open={openTooltip}
          >
            <SoftBox
              sx={{
                height: { mini: "835px", xxs: "875px", md: embedded ? "100%" : "625px" },
                // Below lg the board stacks and this card is the outermost
                // element — no "padding" shorthand here (it was fighting
                // these longhand values for which side wins). Same viewport
                // gutter as the Feed page's cards on mobile; lg+ hands back
                // to the desktop two-column layout (embedded: grid supplies
                // spacing; standalone: greeting card supplies the middle gap).
                paddingTop: { mini: embedded ? 0 : "1.5rem", lg: embedded ? "0px" : "32px" },
                paddingBottom: { mini: 0, lg: embedded ? 0 : "1.5rem" },
                // Standalone uses Feed gutters; embedded board SoftBox owns them.
                paddingLeft: { mini: embedded ? 0 : "1rem", sm: embedded ? 0 : "1.5rem", lg: 0 },
                paddingRight: { mini: embedded ? 0 : "1rem", sm: embedded ? 0 : "1.5rem", lg: embedded ? 0 : "1.5rem" },
                ...(isMidsizeScreen && ({
                  minHeight: "1px !important",
                  height: "unset"
                }))
              }}
            >
              <SoftBox
                sx={{
                  background: "#fff",
                  paddingTop: { mini: "101.25px", xs: "109.25px", md: "75px" },
                  paddingBottom: { mini: "1rem", md: "2rem" },
                  overflowX: "hidden",
                  borderRadius: "12px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: { mini: "960px", xxs: "1000px", md: "850px" },
                  ...(isMidsizeScreen && ({ maxHeight: "unset" }))
                }}
              >
                <SoftBox sx={{ padding: { mini: "8px 16px 0 16px", sm: "0 2rem 0 2rem" }, marginTop: { mini: "-6rem", sm: "-5rem", md: "-3rem" } }}>
                  <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <SoftTypography
                      sx={{
                        fontFamily: "DM Sans",
                        textFillColor: "#0F172A",
                        color: "#0F172A",
                        fontSize: "16px",
                        lineHeight: 1.2,
                        fontWeight: 600,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      My Widgets
                    </SoftTypography>
                    <SoftBox
                      onClick={() => navigate("/widgets")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        // Larger tap target on mobile only, WITHOUT shrinking the
                        // icon itself — the icon's own box is border-box, so
                        // padding directly on it ate into the glyph (it rendered
                        // as a near-invisible dot). Padding lives on this
                        // wrapper instead; the icon keeps its fixed 20px size.
                        ...(isMidsizeScreen && ({ padding: "8px", margin: "-8px" })),
                      }}
                    >
                      <AddIcon sx={{ height: "20px", width: "20px" }} />
                    </SoftBox>
                  </SoftBox>
                  {!isMidsizeScreen && <hr className="my-[0.75rem] divide-slate-300" />}
                </SoftBox>

                {isMidsizeScreen && <hr className="my-[0.75rem] divide-slate-300" />}

                <Box
                  sx={{
                    marginX: "auto",
                    width: "100%",
                    paddingX: { mini: "0.5rem", md: "1rem", xl: "2rem" },
                    ...(isMidsizeScreen && ({ paddingY: "16px" })),
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {panelSlots.map(({ id, widget, Placeholder }) => (
                    <div
                      // A player can pin several dashboards, so the widget key
                      // is no longer unique — the pin's own id is.
                      key={id}
                      style={{ ...(isMidsizeScreen && ({ padding: "0 1rem 0.5rem 1rem" })) }}
                    >
                      {widget ? (
                        <DynamicComponent
                          widget={widget}
                          showPerformBox={true}
                          showGraph={false}
                          isSmallScreen={isMidsizeScreen}
                        />
                      ) : (
                        <Placeholder showPerformBox={true} isSmallScreen={isMidsizeScreen} />
                      )}
                    </div>
                  ))}
                  <Grid container sx={{ paddingTop: "0.5rem" }}>
                    <Grid item mini={12} style={{ ...(isMidsizeScreen && ({ paddingRight: "20px" })),
                    display: 'flex',
                    justifyContent: 'flex-end'
                    }}>
                      <Tooltip title="Boost your company's performance with our custom widgets, designed to streamline operations and enhance efficiency." placement="bottom-end">
                        <SoftTypography sx={{ color: "#667085", fontSize: "12px", fontWeight: 500, cursor: "pointer" }}>
                          {isMidsizeScreen ? <span>&nbsp;</span> : "What is a widget?"}
                        </SoftTypography>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              </SoftBox>
            </SoftBox>
          </CustomWidthTooltip>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Hero;


